// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Cầu postMessage giữa Z-Meet trong iframe và trang cha — ADR-0007 §7.
//
// Trang cha nhúng Z-Meet cần biết chuyện gì đang xảy ra bên trong để dựng giao
// diện của nó: đóng hộp thoại khi cuộc họp kết thúc, hiện chờ khi người dùng
// đang ở phòng chờ, báo lỗi khi vào phòng thất bại. Không có cầu này thì trang
// cha chỉ thấy một hình chữ nhật đen và phải đoán.
//
// # Ba quy tắc, và cả ba đều là bảo mật chứ không phải phong cách
//
// 1. KHÔNG BAO GIỜ postMessage tới '*'. Nó gửi cho bất kỳ trang nào đang nhúng
//    ta, kể cả trang mà người triển khai chưa hề cho phép. Đích đến lấy từ
//    danh sách frame-ancestors của chính server.
//
// 2. Tin nhắn VÀO phải kiểm `event.origin`. Không kiểm thì bất kỳ trang nào
//    trên internet cũng gửi được lệnh `leave` vào cuộc họp của người khác —
//    chỉ cần mở một iframe rồi bắn tin nhắn.
//
// 3. KHÔNG có bí mật trong tin nhắn. Cùng nguyên tắc với kênh sự kiện
//    (ADR-0006 §5): sự kiện là gợi ý, không phải dữ liệu. Không token, không
//    principal ref, không mã tham gia. Trang cha có API key của nó; muốn dữ
//    liệu thì hỏi API, ở đó có phân quyền.

/** Phiên bản giao thức. Trang cha nên kiểm nó trước khi đọc các trường khác. */
export const PROTOCOL_VERSION = 1

/**
 * Nhãn nguồn, để trang cha lọc.
 *
 * Một trang thật nhận postMessage từ đủ thứ — tiện ích trình duyệt, script
 * quảng cáo, iframe khác. Không có nhãn thì trang cha phải đoán, và cách đoán
 * phổ biến nhất (`if (e.data.type)`) sẽ bắt nhầm tin nhắn của người khác.
 */
export const SOURCE = 'zmeet'

export type OutboundType =
  | 'ready' // client đã nạp xong, cầu nối sống
  | 'joining' // đang thử vào phòng
  | 'waiting' // đang ở phòng chờ, host chưa duyệt
  | 'joined' // đã vào phòng
  | 'left' // người dùng tự rời
  | 'ended' // cuộc họp kết thúc
  | 'error' // vào phòng thất bại

export type Outbound = {
  source: typeof SOURCE
  version: number
  type: OutboundType
  /** Chỉ có mặt khi đã biết. Không bao giờ mang token hay principal ref. */
  session_id?: string
  role?: string
  message?: string
}

/** Lệnh trang cha gửi vào. Cố ý ít: xem ghi chú ở `handleInbound`. */
export type InboundType = 'leave'

let origins: string[] = []
let ready = false

/** Trang này có đang nằm trong iframe của ai đó không. */
export function embedded(): boolean {
  try {
    return window.parent !== window
  } catch {
    // Truy cập window.parent xuyên nguồn có thể ném ở vài trình duyệt cũ. Ném
    // nghĩa là CÓ một trang cha khác nguồn.
    return true
  }
}

/**
 * init nạp danh sách nguồn được phép và mở cầu.
 *
 * Không nằm trong iframe thì không gọi API nào: một người dùng mở Z-Meet trực
 * tiếp không nên trả giá một request cho tính năng họ không dùng.
 */
export async function init(onCommand?: (cmd: InboundType) => void): Promise<void> {
  if (ready || !embedded()) return
  ready = true

  try {
    const r = await fetch('/v1/embed-config')
    if (!r.ok) return
    const body = (await r.json()) as { frame_ancestors?: string[] }
    origins = (body.frame_ancestors ?? []).filter((o) => /^https?:\/\//.test(o))
  } catch {
    // Không lấy được cấu hình thì cầu tắt. Đây là mặc định ĐÓNG: thà trang cha
    // không nhận được gì còn hơn ta đoán một đích đến.
    return
  }
  if (origins.length === 0) return

  window.addEventListener('message', (e) => handleInbound(e, onCommand))
  post('ready')
}

/** post gửi một sự kiện tới MỌI nguồn được phép, nêu đích danh từng nguồn. */
export function post(type: OutboundType, extra: Omit<Outbound, 'source' | 'version' | 'type'> = {}) {
  if (!ready || origins.length === 0 || !embedded()) return
  const msg: Outbound = { source: SOURCE, version: PROTOCOL_VERSION, type, ...extra }
  for (const origin of origins) {
    try {
      window.parent.postMessage(msg, origin)
    } catch {
      // Một nguồn hỏng không được làm im nốt các nguồn còn lại.
    }
  }
}

/**
 * handleInbound xử lý lệnh từ trang cha.
 *
 * Chỉ nhận `leave`, và đó là một lựa chọn có chủ đích chứ không phải chưa làm
 * xong. Trang cha VỐN ĐÃ gỡ được iframe bất cứ lúc nào — cho nó một đường rời
 * phòng tử tế là nghiêm ngặt hơn, vì gỡ iframe để lại người dùng treo trong
 * phòng cho tới khi hết thời gian chờ.
 *
 * Những lệnh như "tắt mic", "bật camera" thì khác hẳn: chúng là quyền tác động
 * lên thiết bị của người dùng, và trang cha không phải người dùng. Muốn có thì
 * phải có sự đồng ý tường minh, và đó là một thiết kế riêng.
 */
function handleInbound(e: MessageEvent, onCommand?: (cmd: InboundType) => void) {
  // ⚠️ Kiểm origin TRƯỚC khi đọc bất cứ thứ gì trong e.data. Không kiểm thì
  // bất kỳ trang nào cũng mở được một iframe Z-Meet rồi bắn `leave` vào.
  if (!origins.includes(e.origin)) return

  const data = e.data as { source?: string; type?: string } | null
  if (!data || data.source !== SOURCE) return
  if (data.type === 'leave') onCommand?.('leave')
}

/** allowedOrigins chỉ dành cho test. */
export function allowedOrigins(): string[] {
  return [...origins]
}

/** reset chỉ dành cho test. */
export function reset(list: string[] = []) {
  origins = list
  ready = list.length > 0
}
